import React, { FC, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { useAction } from '@reatom/npm-react';
import { useRouter } from 'next/router';
import { PlayButton } from '@whitesharx/starfall-frontend-ui-kit';
import SayKitWeb from '@whitesharx/starfall-frontend-saykit-web';
import {
  PLAY_BUTTON_ID,
  VIEW_EVENTS,
  useServices,
  t,
  WeeklyRewards,
  useClubs,
  shortenLongNumber,
  Safe,
  usePlayerQuery,
  hasVIP,
} from '@whitesharx/starfall-frontend-core';
import { GameModeController, ClosedClubPopup, Clubs } from '@whitesharx/starfall-frontend-core/clubs_screen';
import '@whitesharx/starfall-frontend-core/clubs_screen.css';

import UserInfo from '~/src/components/UserInfo';
import InfoHeader from '~/src/components/InfoHeader';
import ChessRoyaleBanner from '~/src/components/ChessRoyaleBanner';

const CHANGED_QUERY_KEY = 'changed';
const CHANGED_EMAIL_KEY = 'email';

import useGoGame from '~/src/hooks/useGoGame';
import { AppComponentType, AppTypes } from './_app';
import { parseQueryParams } from '~/src/utils/parseQueryParams';
import { emailChangeCanceledNotify } from '~/src/utils/notifications';
import { bannerContainerStyles } from '~/styles/pages/common.css';
import { buttonsContainerStyles } from '~/styles/pages/clubs.css';
import ClubsSkeletonPage from '~/src/skeletons/Clubs';
import CommonCustomApp from '~/src/components/CustomApp/CommonCustomApp';
import { Tutorial } from '~/src/components/Tutorial';
import { VIPButton } from '~/src/components/VIPButton';
import GameArchiveButton from '~/src/components/GameArchiveButton/GameArchiveButton';
import { updateRightContainerAction } from '~/src/models/layout';

const ClubsRealPage: FC<{ canStartOnboarding: boolean }> = ({ canStartOnboarding }) => {
  const { Analytics, useResponsive, Adsense } = useServices();
  const router = useRouter();
  const updateRightContainer = useAction(updateRightContainerAction);

  const { isDesktop } = useResponsive();
  const goToGame = useGoGame();

  const { data } = usePlayerQuery();

  const [playButtonPulsing, setPlayButtonPulsing] = useState(false);
  const startOnboardingAnimation =
    !localStorage.getItem('onboarding-animation-shown') &&
    !(data?.player.playsCount || data?.player.leaveCount) &&
    canStartOnboarding;
  const hasTutorial =
    SayKitWeb.featureFlags.onboarding_tutorial === 1 && !localStorage.getItem('onboarding-animation-shown');

  const { mode, setMode, club, isDisabledStartGame, isClubClosed, neededPointsToOpen, onPlay } = useClubs();

  const gamePrice = club?.buyInCoins ? shortenLongNumber(club.buyInCoins) : 0;

  useEffect(() => {
    const changedValue = parseQueryParams(router.asPath, CHANGED_QUERY_KEY);
    if (changedValue === CHANGED_EMAIL_KEY) {
      emailChangeCanceledNotify();
      router.replace(router.asPath, { query: {} });
    }
  }, []);

  useEffect(() => {
    Analytics.sendEvent(VIEW_EVENTS.clubs);
  }, []);

  const startGame = () => {
    onPlay({ goToGame });

    if (startOnboardingAnimation) {
      localStorage.setItem('onboarding-animation-shown', 'true');
    }
  };

  const renderDesktopPlayButton = () => {
    const playButton = (
      <PlayButton
        price={gamePrice}
        onClick={startGame}
        disabled={isDisabledStartGame}
        id={PLAY_BUTTON_ID}
        pulse={hasTutorial ? playButtonPulsing : startOnboardingAnimation}
        blockContent={hasTutorial ? false : startOnboardingAnimation}
        blurContent={hasTutorial ? false : startOnboardingAnimation}
      />
    );

    if (isClubClosed) {
      return (
        <ClosedClubPopup.Desktop neededPointsToOpen={neededPointsToOpen}>
          <div>{playButton}</div>
        </ClosedClubPopup.Desktop>
      );
    }
    return playButton;
  };

  const rightContainer = useMemo(() => {
    if (isDesktop) {
      return (
        <>
          <UserInfo />
          <div className={buttonsContainerStyles}>
            <GameModeController onChange={setMode} value={mode} />
            {renderDesktopPlayButton()}
          </div>
          {Adsense.renderBanner({
            placement: 'clubs_screen_banner_right',
            slot: 'bannerRight',
            format: 'rectangle',
          })}
          <div className={bannerContainerStyles}>
            <ChessRoyaleBanner />
          </div>
        </>
      );
    }
  }, [isDesktop, isClubClosed, isDisabledStartGame, onPlay, gamePrice, startOnboardingAnimation, playButtonPulsing]);

  useEffect(() => {
    updateRightContainer(rightContainer);
  }, [rightContainer]);

  return (
    <>
      <Head>
        <title>{t('browser-title.index')}</title>
      </Head>

      {hasTutorial && startOnboardingAnimation && (
        <Tutorial blur={!!startOnboardingAnimation} onLastStep={() => setPlayButtonPulsing(true)} />
      )}

      {!isDesktop && <InfoHeader key="info-header" />}
      <Clubs
        startGame={startGame}
        isOnboarding={hasTutorial ? playButtonPulsing : startOnboardingAnimation}
        noAdsAvailable={data?.player ? !hasVIP(data?.player) : false}
        LeftTopSlot={
          <>
            <GameArchiveButton />
            {!hasVIP(data?.player) && <VIPButton />}
          </>
        }
      />
      <WeeklyRewards />
      <Safe />
    </>
  );
};

const ClubsPage: FC<AppTypes & { canStartOnboarding: boolean }> & AppComponentType = (props) =>
  props.skeleton ? <ClubsSkeletonPage /> : <ClubsRealPage {...props} />;

ClubsPage.render = (props) => <CommonCustomApp {...props} />;

export default ClubsPage;
